import { Descriptions, Popover, Space, Tooltip } from "antd"
import { centsToDollarsDisplay } from "../../displayFormatters"
import { fromNow } from "../invoiceModel"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleDown, faCircleQuestion, faCircleUp, faDiamondExclamation } from "@fortawesome/pro-solid-svg-icons"
import { gold, green, red } from "@ant-design/colors"
import VSpace from "../../common/VSpace"
import { InvoiceSummary } from "../../../domain/invoicing/invoice"
import { hasValue } from "../../../helpers/nullable"

type Props = {
  invoiceSummary: InvoiceSummary
}

const SubtotalDisplay = ({ invoiceSummary }: Props) => {
  const isExported = hasValue(invoiceSummary.qboInvoiceId) && invoiceSummary.qboInvoiceId.length > 0
  const alertQboAmount = invoiceSummary.qboInvoiceExists && invoiceSummary.qboAmountWithoutTax !== invoiceSummary.amount
  const qboAmountIsLess = invoiceSummary.qboInvoiceExists && invoiceSummary.qboAmountWithoutTax < invoiceSummary.amount

  if (isExported && !invoiceSummary.qboInvoiceExists) {
    return <Tooltip title="Missing QuickBooks data. The invoice may have been deleted from QuickBooks.">
      <Space>
        <FontAwesomeIcon icon={faCircleQuestion} color={gold[7]} />
        <span>{centsToDollarsDisplay(invoiceSummary.amount)}</span>
      </Space>
    </Tooltip>
  }

  if (alertQboAmount) {
    return <Popover
      title={<Space><FontAwesomeIcon icon={faDiamondExclamation} color={gold[7]} /> QuickBooks Invoice Amount Mismatch</Space>}
      content={<VSpace style={{ maxWidth: '400px' }}>
        <span>The QuickBooks Invoice shows a different amount than what was originally created through SynkedUP.</span>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label={<span>QuickBooks Amount<br /><small>(Last updated {fromNow(invoiceSummary.lastUpdatedAt)})</small></span>}>
            {centsToDollarsDisplay(invoiceSummary.qboAmountWithoutTax)}
          </Descriptions.Item>
          <Descriptions.Item label="SynkedUP Amount">
            {centsToDollarsDisplay(invoiceSummary.amount)}
          </Descriptions.Item>
          <Descriptions.Item label="Difference">
            <Space>
              {qboAmountIsLess
                ? <FontAwesomeIcon icon={faCircleDown} color={red[7]} size="lg" />
                : <FontAwesomeIcon icon={faCircleUp} color={green.primary} size="lg" />
              }
              {centsToDollarsDisplay((invoiceSummary.qboAmountWithoutTax ?? 0) - invoiceSummary.amount)}
            </Space>
          </Descriptions.Item>
        </Descriptions>
        {qboAmountIsLess
          ? <span>This means you may not end up charging enough to recover all your costs for this job!</span>
          : <span>This means you're charging an additional amount beyond what is applied to the workareas for this invoice.</span>
        }
      </VSpace>}
    >
      <Space>
        <FontAwesomeIcon icon={faDiamondExclamation} color={gold[7]} />
        <span style={{ color: qboAmountIsLess ? red[7] : undefined }}>{centsToDollarsDisplay(invoiceSummary.amount)}</span>
      </Space>
    </Popover>
  }

  return <>{centsToDollarsDisplay(invoiceSummary.amount)}</>
}

export default SubtotalDisplay
